import { useMemo } from "react";
import type { XPost, XPostAuthor } from "../types";
import { X } from "lucide-react";
import { extractHashtags } from "../lib/utils";
import { HashtagDisplay } from "./ui/hashtag-display";
import { MediaRenderer, getMediaInfo } from "./MediaRenderer";

interface ThreadPopupProps {
  author: XPostAuthor;
  threads: XPost[];
  originalTweet: XPost;
  onClose: () => void;
}

export const ThreadPopup = ({ author, threads, originalTweet, onClose }: ThreadPopupProps) => {
  const originalMediaInfo = useMemo(() => getMediaInfo(originalTweet), [originalTweet]);

  return (
    <div className="fixed inset-0 flex items-stretch z-50">
      {/* Left semi-transparent area (25%) */}
      <div className="w-1/4 bg-black/85" />

      {/* Center content area (50%) */}
      <div className="w-1/2 bg-slate-800 min-h-screen overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
          <h2 className="text-xl font-semibold text-white">Thread by {author?.user?.name || "Unknown User"}</h2>
          <button onClick={onClose} className="text-gray-400 bg-transparent hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {/* Original Tweet */}
          <div className="mb-8 border-b border-gray-700 pb-6">
            <div className="flex items-start space-x-3 mb-2">
              <img
                src={author?.user?.profile_image || "/assets/placeholder.jpg"}
                alt={author?.user?.name || "User"}
                className="w-10 h-10 rounded-full"
                onError={(e) => {
                  e.currentTarget.src = "/assets/placeholder.jpg";
                }}
              />
              <div>
                <div className="font-semibold text-white">{author?.user?.name || "Unknown User"}</div>
                <div className="text-sm text-gray-400">@{author?.user?.username || "unknown"}</div>
              </div>
            </div>
            {(() => {
              const { hashtags, cleanedText } = extractHashtags(originalTweet.content || '');
              return (
                <>
                  <p className="text-white text-sm leading-relaxed whitespace-pre-line pl-13">
                    {cleanedText || "No content available"}
                  </p>
                  {hashtags.length > 0 && (
                    <div className="pl-13 mt-2">
                      <HashtagDisplay hashtags={hashtags} />
                    </div>
                  )}
                </>
              );
            })()}
            {originalTweet.media_urls && originalTweet.media_urls.length > 0 && (
              <div className="mt-2 pl-13">
                <div className="relative min-h-[200px] max-h-[500px] w-full">
                  <MediaRenderer 
                    mediaInfo={originalMediaInfo}
                    maxHeight="500px"
                    className="w-full"
                    style={{
                      aspectRatio: 'auto',
                      minHeight: '200px',
                      maxHeight: '500px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Thread Replies */}
          <div className="space-y-6">
            {threads.map((thread, index) => {
              const { name, username, profile_image } = author.user;
              const threadMediaInfo = useMemo(() => getMediaInfo(thread), [thread]);
              
              return (
                <div key={index} className="mb-6 last:mb-0 border-b border-gray-700 pb-6 last:border-0">
                  <div className="flex items-start space-x-3 mb-2">
                    <img
                      src={profile_image || "/assets/placeholder.jpg"}
                      alt={name || "User"}
                      className="w-10 h-10 rounded-full"
                      onError={(e) => {
                        e.currentTarget.src = "/assets/placeholder.jpg";
                      }}
                    />
                    <div>
                      <div className="font-semibold text-white">{name || "Unknown User"}</div>
                      <div className="text-sm text-gray-400">@{username || "unknown"}</div>
                    </div>
                  </div>
                  {(() => {
                    const { hashtags, cleanedText } = extractHashtags(thread.content || '');
                    return (
                      <>
                        <p className="text-white text-sm leading-relaxed whitespace-pre-line pl-13">
                          {cleanedText || "No content available"}
                        </p>
                        {hashtags.length > 0 && (
                          <div className="pl-13 mt-2">
                            <HashtagDisplay hashtags={hashtags} />
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {thread.media_urls && thread.media_urls.length > 0 && (
                    <div className="mt-2 pl-13">
                      <div className="relative min-h-[200px] max-h-[500px] w-full">
                        <MediaRenderer 
                          mediaInfo={threadMediaInfo}
                          maxHeight="500px"
                          className="w-full"
                          style={{
                            aspectRatio: 'auto',
                            minHeight: '200px',
                            maxHeight: '500px'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right semi-transparent area (25%) */}
      <div className="w-1/4 bg-black/85" />
    </div>
  );
}; 